import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  TrackByFunction,
  ViewChild,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatMenu, MatMenuModule } from '@angular/material/menu';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { TNil } from '@app/app.interface';
import { HasPermissionDirective } from '@core/directives/has-permission.directive';
import { NumberSuffixPipe } from '@core/pipes/number-suffix.pipe';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { TableStateService } from '@core/services/table-state.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { ECampaignStatus } from '@enums/campaign.enum';
import { EEventType } from '@enums/event-tracking.enum';
import { ICampaignAnalyticsApi } from '@interfaces/campaign-analytics-api.interface';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CampaignDetailsStore } from '@pages/campaigns/campaign-details/campaign-details.store';
import { TCampaignApi, TCampaignTable } from '@pages/campaigns/campaigns.interface';
import { CampaignsStore, IFetchCampaignsParams } from '@pages/campaigns/campaigns.store';
import { CreateCampaignDialogComponent } from '@pages/campaigns/create-campaign-dialog/create-campaign-dialog.component';
import { ICreateCampaignDialogData } from '@pages/campaigns/create-campaign-dialog/create-campaign-dialog.interface';
import { ICustomer } from '@pages/customers/customers.interface';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DatatableBaseComponent } from '@shared/components/datatable-base/datatable-base.component';
import { DatatableComponent } from '@shared/components/datatable/datatable.component';
import {
  IDataRefreshRequestParams,
  IDatatableConfiguration,
  IRowClickEvent,
  TDatatableColumn,
} from '@shared/components/datatable/datatable.interface';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import {
  IProfileMultiSelectState,
  ProfileMultiSelectFilterService,
} from '@shared/components/profile-general-filters/profile-multi-select-filter.service';
import { ProgressBarComponent } from '@shared/components/progress-bar/progress-bar.component';
import {
  ISkeletonLoaderConfig,
  ISkeletonLoaderFilter,
} from '@shared/components/skeleton-loader/skeleton-loader.interface';
import { StatusBadgeComponent } from '@shared/components/status-badge/status-badge.component';
import { TextClampTooltipDirective } from '@shared/directives/text-clamp-tooltip.directive';
import { CampaignAnalyticsUtilitiesService } from '@utilities/campaign-analytics-utilities.service';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { camelCase } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { combineLatest, distinctUntilChanged, filter, map, startWith } from 'rxjs';
import { IAnalyticsBatchState, TCampaignDatatableColumn } from './campaigns-datatable.interface';
import { CampaignsDatatableService } from './campaigns-datatable.service';
import { CampaignsDatatableStore } from './campaigns-datatable.store';

type TCampaignStorageMode = 'admin' | 'creator' | 'default';
type TCampaignStorageKey = `${TCampaignStorageMode}:${string}`;

@Component({
  imports: [
    HasPermissionDirective,
    IconBadgeComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatMenuModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    ReactiveFormsModule,
    StatusBadgeComponent,
    TranslateModule,
    MatDialogModule,
    DatatableComponent,
    NumberSuffixPipe,
    ProgressBarComponent,
    NgxSkeletonLoaderModule,
    TextClampTooltipDirective,
    MatTooltipModule,
    IconComponent,
  ],
  providers: [CampaignDetailsStore, CampaignsDatatableStore],
  selector: 'app-campaigns-datatable',
  styleUrl: './campaigns-datatable.component.scss',
  templateUrl: './campaigns-datatable.component.html',
})
export class CampaignsDatatableComponent extends DatatableBaseComponent implements OnInit {
  public readonly campaigns = input.required<TCampaignApi[] | null>();
  public readonly total = input.required<number>();
  public readonly campaignsLoading = input.required<boolean>();

  public readonly showCreateButton = input<boolean>(true);
  public readonly adminMode = input<boolean>(false);
  public readonly creatorMode = input<boolean>(false);
  public readonly requestFilters = input<Record<string, unknown> | null>(null);
  public readonly requestJoins = input<string[]>([]);
  public readonly columns = input<TCampaignDatatableColumn[]>([]);
  public readonly creatorId = input<string | TNil>(null);

  public readonly dataRefreshRequest = output<IDataRefreshRequestParams>();

  public readonly ECampaignStatus = ECampaignStatus;
  public readonly EAnalyticsStatus = EAnalyticsStatus;

  public readonly loadedTableData = signal<boolean>(false);
  public readonly isRedirecting = signal<boolean>(false);
  public readonly isAnalyticsCalculated = signal<boolean>(false);
  public readonly dataSource = signal<MatTableDataSource<TCampaignTable>>(new MatTableDataSource<TCampaignTable>([]));
  // Every analytics batch re-emits the rows (updateTableWithAnalytics) - keyed by id, the rows stay
  // mounted and the campaign/customer images don't reload (blink) on each batch.
  public readonly trackByCampaignId: TrackByFunction<TCampaignTable> = (_index, row) => row.id;

  public datatableColumns: TDatatableColumn<TCampaignTable, string>[] = [
    { id: 'name' },
    { id: 'status' },
    { dataViewStrategy: (row: TCampaignTable) => this.dateUtils.formatDate(row.start), id: 'startFormatted' },
    { dataViewStrategy: (row: TCampaignTable) => this.dateUtils.formatDate(row.end), id: 'endFormatted' },
    { id: 'customer' },
    { id: 'budget' },
    { id: 'pay' },
    { id: 'ctaKpiStatus' },
    { dataViewStrategy: () => '', id: 'actions' },
  ];

  public datatableConfig!: IDatatableConfiguration<TCampaignTable>;
  public isFetchingAnalytics = true;

  private readonly router = inject(Router);
  private readonly eventTrackingService = inject(EventTrackingService);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(CampaignsStore);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly campaignsDatatableStore = inject(CampaignsDatatableStore);
  private readonly campaignsDatatableService = inject(CampaignsDatatableService);
  private readonly campaignDetailsStore = inject(CampaignDetailsStore);
  private readonly campaignAnalyticsUtilities = inject(CampaignAnalyticsUtilitiesService);
  private readonly multiSelectFilterService = inject(ProfileMultiSelectFilterService);
  private readonly tableStateService: TableStateService = inject(TableStateService);

  public skeletonLoaderConfig!: ISkeletonLoaderConfig;

  public readonly users = this.campaignsDatatableStore.users$;
  public readonly usersLoading = this.campaignsDatatableStore.usersLoading$;
  public readonly createdByIds = signal<Set<string>>(new Set<string>());
  public readonly selectionState = signal<IProfileMultiSelectState<string>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<string>(),
  );
  public readonly searchControl = new FormControl<string>('', { nonNullable: true });
  public readonly selectionControl = new FormControl<string[]>([], { nonNullable: true });
  public readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(startWith('')), { initialValue: '' });

  public readonly teams = this.campaignsDatatableStore.teams$;
  public readonly teamsLoading = this.campaignsDatatableStore.teamsLoading$;
  public readonly teamIds = signal<Set<string>>(new Set<string>());
  public readonly teamSelectionState = signal<IProfileMultiSelectState<string>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<string>(),
  );
  public readonly teamSearchControl = new FormControl<string>('', { nonNullable: true });
  public readonly teamSelectionControl = new FormControl<string[]>([], { nonNullable: true });
  public readonly teamSearchQuery = toSignal(this.teamSearchControl.valueChanges.pipe(startWith('')), {
    initialValue: '',
  });
  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly displayedUsers = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const lang = this.languageChange();
    const userList = this.users();
    const sorted = [...userList].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', lang));

    if (!query) {
      return sorted;
    }

    return sorted.filter((user) => user.name?.toLowerCase().includes(query));
  });

  public readonly displayedTeams = computed(() => {
    const query = this.teamSearchQuery().trim().toLowerCase();
    const lang = this.languageChange();
    const teamList = this.teams();
    const sorted = [...teamList].sort((a, b) => a.name.localeCompare(b.name, lang));

    if (!query) {
      return sorted;
    }

    return sorted.filter((team) => team.name.toLowerCase().includes(query));
  });

  public readonly emptyLabel = computed(() => {
    this.languageChange();
    const totalUsers = this.users().length;

    if (totalUsers === 0) {
      return this.translate.instant('shared.all');
    }

    return `${this.translate.instant('shared.all')} (${totalUsers})`;
  });

  public readonly triggerLabel = computed(() => {
    this.languageChange();
    const userList = this.users();
    const selectedIds = this.createdByIds();

    if (selectedIds.size === 0) {
      return this.emptyLabel();
    }

    const orderedSelectedUsers = userList.filter((user) => selectedIds.has(user.id));

    return this.multiSelectFilterService.buildSelectionSummary(
      orderedSelectedUsers[0]?.name ?? '',
      orderedSelectedUsers.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  public readonly teamEmptyLabel = computed(() => {
    this.languageChange();
    const totalTeams = this.teams().length;

    if (totalTeams === 0) {
      return this.translate.instant('shared.all');
    }

    return `${this.translate.instant('shared.all')} (${totalTeams})`;
  });

  public readonly teamTriggerLabel = computed(() => {
    this.languageChange();
    const teamList = this.teams();
    const selectedIds = this.teamIds();

    if (selectedIds.size === 0) {
      return this.teamEmptyLabel();
    }

    const orderedSelectedTeams = teamList.filter((team) => selectedIds.has(team.id));

    return this.multiSelectFilterService.buildSelectionSummary(
      orderedSelectedTeams[0]?.name ?? '',
      orderedSelectedTeams.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  @ViewChild('createdByFilter', { static: true })
  public readonly createdByFilterTemplate!: TemplateRef<unknown>;

  @ViewChild('teamFilter', { static: true })
  public readonly teamFilterTemplate!: TemplateRef<unknown>;

  private readonly menu = viewChild<MatMenu>('menu');

  private readonly processedCampaignIds = signal<Set<string>>(new Set<string>());

  private tableData: TCampaignTable[] = [];
  // Fed by syncTableDataFromStore (store.campaigns$(), this datatable's own paginated fetch) via
  // prepareTableData, and cancellable by onFilterEmitted - see IAnalyticsBatchState's own doc
  // comment for why this is a separate state object from analyticsSourceBatchState below.
  private tableBatchState: IAnalyticsBatchState = { isProcessing: true, timeoutId: null };
  // Fed by syncAnalyticsSourceFromInput (campaigns() input) via prepareAnalyticsSourceData - only
  // ever independent from tableBatchState on CreatorDetailsComponent/CustomerDetailsComponent,
  // where campaigns() is fed from a different, independently-fetched source (profileCampaigns$()).
  // Never touched by onFilterEmitted - filters don't apply to that independent source.
  private analyticsSourceBatchState: IAnalyticsBatchState = { isProcessing: true, timeoutId: null };
  private datatableParams: IDataRefreshRequestParams | null = null;

  constructor() {
    super();

    this.skeletonLoaderConfig = this.buildSkeletonLoaderConfig();
    this.setDatatableConfig();

    if (this.permissionService.hasPermission(this.EPermissions.userReadOwn)) {
      this.campaignsDatatableStore.fetchUsers();
    }

    if (this.permissionService.hasPermission(this.EPermissions.teamReadOwn)) {
      this.campaignsDatatableStore.fetchTeams();
    }

    effect(() => this.syncTableDataFromStore());

    effect(() => this.syncAnalyticsSourceFromInput());

    effect(() => {
      const analyticsMap = this.campaignDetailsStore.campaignAnalyticsMap$();

      if (analyticsMap && Object.keys(analyticsMap).length > 0) {
        this.updateTableWithAnalytics();
        this.loadedTableData.set(true);
      }
    });

    /**
     * !this.store.loading$() alone used to be the correct "is the fetch behind campaigns() still
     * running" check for every page that uses this component, because campaigns() was always fed
     * by the same store.campaigns$()/loading$() pair. CreatorDetailsComponent/CustomerDetailsComponent
     * now feed campaigns() from the independently-fetched profileCampaigns$()/profileCampaignsLoading$()
     * instead (see syncAnalyticsSourceFromInput below), so loading$() alone no longer reflects
     * whether campaigns() itself is still loading for those two pages - checking both flags covers
     * every page: profileCampaignsLoading$() stays false where it's never used (CampaignsComponent/
     * AdminCampaignsComponent), so this is equivalent to the old check there, and it's the flag that
     * actually matters for CreatorDetailsComponent/CustomerDetailsComponent.
     */
    effect(() => {
      const campaigns = this.campaigns();

      if (campaigns && campaigns.length === 0 && !this.store.loading$() && !this.store.profileCampaignsLoading$()) {
        this.loadedTableData.set(true);
      }
    });

    effect(() => {
      this.isFetchingAnalytics = this.store.fetchingCampaignsForAnalytics$() || !this.isAnalyticsCalculated();
    });

    effect(() => {
      const controlValues = this.selectionState().uiSelectedValues;

      if (!this.arraysEqual(this.selectionControl.value ?? [], controlValues)) {
        this.selectionControl.setValue(controlValues, { emitEvent: false });
      }
    });

    effect(() => {
      const controlValues = this.teamSelectionState().uiSelectedValues;

      if (!this.arraysEqual(this.teamSelectionControl.value ?? [], controlValues)) {
        this.teamSelectionControl.setValue(controlValues, { emitEvent: false });
      }
    });
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      combineLatest({
        datatable: this.datatableParams$.pipe(filter(Boolean)),
      })
        .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
        .subscribe(({ datatable }) => {
          this.isFetchingAnalytics = true;
          this.datatableParams = datatable;

          this.store.fetchCampaigns(this.prepareFetchCampaignsParams(datatable));
        }),
    );

    if (this.permissionService.hasPermission(this.EPermissions.teamReadOwn)) {
      this.datatableConfig = {
        ...this.datatableConfig,
        filters: [
          ...(this.datatableConfig.filters ?? []),
          {
            customFilterTemplate: this.teamFilterTemplate,
            key: 'team_id__in',
            label: this.translate.instant('field.team'),
            multiple: true,
            placeholder: this.translate.instant('shared.all'),
          },
        ],
      };
    }

    if (this.permissionService.hasPermission(this.EPermissions.userReadOwn)) {
      this.datatableConfig = {
        ...this.datatableConfig,
        filters: [
          ...(this.datatableConfig.filters ?? []),
          {
            customFilterTemplate: this.createdByFilterTemplate,
            key: 'created_by__in',
            label: this.translate.instant('field.createdBy'),
            multiple: true,
            placeholder: this.translate.instant('shared.all'),
          },
        ],
      };
    }
  }

  public onEditClick(event: Event, campaign: TCampaignTable): void {
    event.stopPropagation();

    this.eventTrackingService.track(EEventType.campaignCreationOpened, {
      campaignId: campaign.id,
      source: 'campaign_list_edit',
    });

    this.router.navigate(['/campaigns/form', campaign.id], {
      state: { adminMode: this.adminMode() },
    });
  }

  public onDeleteClick(event: Event, campaign: TCampaignTable): void {
    event.stopPropagation();

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('shared.cancel'),
        confirmText: this.translate.instant('shared.delete'),
        message: this.translate.instant('pages.campaigns.datatable.deleteCampaignMessage', {
          name: campaign.name,
        }),
        title: this.translate.instant('pages.campaigns.datatable.deleteCampaign'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((): void => {
        this.store.deleteCampaign({
          campaignId: campaign.id!,
          params: this.prepareFetchCampaignsParams(this.datatableParams),
        });

        this.menu()?.closed.emit();
      });
  }

  public onCopyClick(event: Event, campaign: TCampaignTable): void {
    event.stopPropagation();

    const copyDialogData: ICreateCampaignDialogData = {
      adminMode: this.adminMode(),
      mode: 'copy',
      sourceCampaign: {
        iconId: campaign.iconId ?? null,
        id: campaign.id,
        name: campaign.name,
      },
    };

    this.dialog.open(CreateCampaignDialogComponent, {
      autoFocus: true,
      data: copyDialogData,
      minWidth: '700px',
    });

    this.menu()?.closed.emit();
  }

  public onCustomerClick(event: Event, customer: ICustomer): void {
    event.stopPropagation();

    WindowRedirectService.openUrlInNewTab(`/customers/${customer.id}`);
  }

  public onRowClick(row: IRowClickEvent<TCampaignTable, string>): void {
    if (
      !this.permissionService.hasAnyPermission([
        this.EPermissions.campaignReadOwn,
        this.EPermissions.campaignReadAll,
        this.EPermissions.campaignBriefingSelf,
      ])
    ) {
      return;
    }

    const { event, row: campaign } = row;

    const isNewTab = event?.metaKey || event?.ctrlKey;
    const isDraftFormEdit = campaign.status === ECampaignStatus.draft && !this.creatorMode();

    if (!isDraftFormEdit) {
      // The draft branch below opens the creation form, not the analytics view - #18 only applies
      // to the other 3 branches (admin/briefing/normal), all of which land on CampaignDetailsComponent.
      this.eventTrackingService.track(EEventType.campaignAnalyticsOpened, {
        campaignId: campaign.id,
        isBriefing: this.creatorMode() && !!campaign.creatorCampaignExportId,
        isReport: false,
        source: 'campaign_list',
      });
    } else {
      this.eventTrackingService.track(EEventType.campaignCreationOpened, {
        campaignId: campaign.id,
        source: 'campaign_list_row',
      });
    }

    let url: string;

    if (isDraftFormEdit) {
      url = `/campaigns/form/${campaign.id}`;
    } else if (this.adminMode()) {
      url = `/admin/campaigns/${campaign.id}`;
    } else if (this.creatorMode() && campaign.creatorCampaignExportId) {
      url = `/campaigns/briefing/creator/${campaign.creatorCampaignExportId}`;
    } else {
      url = `/campaigns/${campaign.id}`;
    }

    if (isNewTab) {
      WindowRedirectService.openUrlInNewTab(url);
    } else {
      this.isRedirecting.set(true);

      if (campaign.status === ECampaignStatus.draft && !this.creatorMode()) {
        this.router.navigate(['/campaigns/form', campaign.id], {
          state: { adminMode: this.adminMode() },
        });
      } else if (this.adminMode()) {
        this.router.navigate(['/admin/campaigns', campaign.id]);
      } else if (this.creatorMode() && campaign.creatorCampaignExportId) {
        this.router.navigate(['/campaigns/briefing/creator', campaign.creatorCampaignExportId], {
          state: { creatorMode: true, creatorName: campaign.campaignCreators?.[0]?.creator?.name ?? '' },
        });
      } else {
        this.router.navigate(['/campaigns', campaign.id]);
      }
    }
  }

  public onDataRefreshRequest(params: IDataRefreshRequestParams): void {
    this.dataRefreshRequest.emit(params);
    this.datatableParams$.next(params);
  }

  public onSelectionChange(event: MatSelectChange<string[]>): void {
    const visibleSelectedValues: string[] = event.value ?? [];
    const query = this.searchQuery().trim().toLowerCase();
    const hiddenSelected: string[] = query
      ? this.selectionState().uiSelectedValues.filter((v) => !this.displayedUsers().some((user) => user.id === v))
      : [];

    const nextSelectedValues: string[] = [...new Set([...visibleSelectedValues, ...hiddenSelected])];

    const nextState =
      nextSelectedValues.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<string>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedValues);

    this.selectionState.set(nextState);
    this.emitCreatedByFilterChange();
  }

  public onSelectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(
      this.multiSelectFilterService.createExplicitSelectionState(this.users().map((user) => user.id)),
    );
    this.emitCreatedByFilterChange();
  }

  public onUnselectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<string>());
    this.emitCreatedByFilterChange();
  }

  public onTeamSelectionChange(event: MatSelectChange<string[]>): void {
    const visibleSelectedValues: string[] = event.value ?? [];
    const query = this.teamSearchQuery().trim().toLowerCase();
    const hiddenSelected: string[] = query
      ? this.teamSelectionState().uiSelectedValues.filter((v) => !this.displayedTeams().some((team) => team.id === v))
      : [];

    const nextSelectedValues: string[] = [...new Set([...visibleSelectedValues, ...hiddenSelected])];

    const nextState =
      nextSelectedValues.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<string>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedValues);

    this.teamSelectionState.set(nextState);
    this.emitTeamFilterChange();
  }

  public onTeamSelectAll(event: Event): void {
    event.stopPropagation();

    this.teamSelectionState.set(
      this.multiSelectFilterService.createExplicitSelectionState(this.teams().map((team) => team.id)),
    );
    this.emitTeamFilterChange();
  }

  public onTeamUnselectAll(event: Event): void {
    event.stopPropagation();

    this.teamSelectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<string>());
    this.emitTeamFilterChange();
  }

  private emitCreatedByFilterChange(): void {
    this.createdByIds.set(
      this.multiSelectFilterService.normalizeValuesForEmission(
        this.selectionState(),
        this.users().map((user) => user.id),
      ),
    );

    this.onFilterEmitted();
  }

  private emitTeamFilterChange(): void {
    this.teamIds.set(
      this.multiSelectFilterService.normalizeValuesForEmission(
        this.teamSelectionState(),
        this.teams().map((team) => team.id),
      ),
    );

    this.onFilterEmitted();
  }

  private onFilterEmitted(): void {
    if (this.tableBatchState.timeoutId !== null) {
      clearTimeout(this.tableBatchState.timeoutId);
      this.tableBatchState.timeoutId = null;
    }

    this.tableBatchState.isProcessing = false;

    if (this.datatableParams) {
      this.isAnalyticsCalculated.set(false);
      this.isFetchingAnalytics = true;
      this.store.fetchCampaigns(this.prepareFetchCampaignsParams(this.datatableParams));
    }
  }

  private arraysEqual(a: string[], b: string[]): boolean {
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  private buildFilterSkeletons(): ISkeletonLoaderFilter[] {
    const filterSkeletons: ISkeletonLoaderFilter[] = [{ inputClass: '!min-w-[220px]' }];

    if (this.permissionService.hasPermission(this.EPermissions.userReadOwn)) {
      filterSkeletons.push({ inputClass: '!min-w-[220px]' });
    }

    if (this.permissionService.hasPermission(this.EPermissions.teamReadOwn)) {
      filterSkeletons.push({ inputClass: '!min-w-[220px]' });
    }

    return filterSkeletons;
  }

  private buildSkeletonLoaderConfig(): ISkeletonLoaderConfig {
    const filterSkeletons = this.buildFilterSkeletons();

    return {
      columns: [
        {
          borderRadius: '4px',
          circleSize: '40px',
          className: 'w-[24%]',
          columnPadding: '0 8px',
          hasText: true,
          iconRadius: '100px',
          rowPadding: '8px 0',
          textWidth: '200px',
          type: 'icon',
        },
        {
          borderRadius: '100px',
          circleSize: '20px',
          className: 'w-[8%]',
          height: '24px',
          textWidth: '60px',
          type: 'text',
        },
        {
          borderRadius: '4px',
          circleSize: '20px',
          className: 'w-[11%]',
          hasText: true,
          textWidth: '80px',
          type: 'icon',
        },
        {
          borderRadius: '4px',
          circleSize: '20px',
          className: 'w-[11%]',
          hasText: true,
          textWidth: '80px',
          type: 'icon',
        },
        {
          borderRadius: '4px',
          circleSize: '16px',
          className: 'w-[15%]',
          hasText: true,
          textWidth: '60px',
          type: 'icon',
        },
        { borderRadius: '4px', className: 'w-[11%]', textWidth: '40px', type: 'text' },
        { borderRadius: '8px', className: 'w-[13%]', height: '8px', textWidth: '108px', type: 'text' },
        {
          borderRadius: '100vw',
          circleSize: '24px',
          className: 'w-[7%] flex justify-start',
          type: 'icon',
          width: '0',
        },
      ],
      filterContainerClass: 'flex items-center gap-5',
      filters: filterSkeletons,
      rows: 5,
      showFilters: true,
      showHeaderControls: true,
      showSearchField: true,
    };
  }

  private fetchAnalyticsForAllCampaigns(campaigns: TCampaignApi[], batchState: IAnalyticsBatchState): void {
    const campaignIds = campaigns.map((campaign) => campaign.id);

    if (!campaignIds?.length) {
      return;
    }

    batchState.isProcessing = true;

    batchState.timeoutId = setTimeout(() => {
      this.processCampaignBulkBatch(campaignIds, campaigns, 0, 25, batchState);
    }, 300);
  }

  private processCampaignBulkBatch(
    campaignIds: string[],
    campaigns: TCampaignApi[],
    startIndex: number,
    batchSize: number,
    batchState: IAnalyticsBatchState,
  ): void {
    if (startIndex >= campaignIds.length) {
      batchState.isProcessing = false;

      return;
    }

    const endIndex = Math.min(startIndex + batchSize, campaignIds.length);
    const currentBatch = campaignIds.slice(startIndex, endIndex);
    const nonLiveCampaignIds = campaigns.slice(startIndex, endIndex).reduce((acc, campaign) => {
      if (campaign.status === ECampaignStatus.draft || campaign.status === ECampaignStatus.planned) {
        acc.push(campaign.id);
      }

      return acc;
    }, [] as string[]);

    this.campaignDetailsStore.fetchCampaignAnalyticsBulk({
      adminMode: this.adminMode(),
      creatorMode: this.creatorMode(),
      ids: currentBatch,
    });

    if (nonLiveCampaignIds.length && batchState.isProcessing) {
      this.store.fetchCampaigns(
        this.prepareFetchCampaignsParams(
          { ...this.datatableParams, page: 0 } as IDataRefreshRequestParams,
          nonLiveCampaignIds,
          true,
        ),
      );
    }

    if (endIndex < campaignIds.length && batchState.isProcessing) {
      setTimeout(() => {
        this.processCampaignBulkBatch(campaignIds, campaigns, endIndex, batchSize, batchState);
      }, 500);
    } else {
      batchState.isProcessing = false;
    }
  }

  private updateTableWithAnalytics(): void {
    const analyticsMap = this.campaignDetailsStore.campaignAnalyticsMap$();
    const nonLiveCampaignsMap = this.store.nonLiveCampaignsMap$();
    const updatedCampaignIds = new Set<string>();
    const campaigns = this.campaigns();
    let allAnalyticsDataReceived = true;

    if (campaigns?.length) {
      campaigns.forEach((campaign) => {
        const analytics = analyticsMap[campaign.id];

        if (analytics === undefined) {
          allAnalyticsDataReceived = false;
        }

        updatedCampaignIds.add(campaign.id);
        this.updateCampaignAnalytics(campaign.id, analytics, nonLiveCampaignsMap);
      });
    }

    this.processedCampaignIds.set(updatedCampaignIds);

    // campaigns() (read above) is store.campaigns$() itself on CampaignsComponent/
    // AdminCampaignsComponent (only tableBatchState ever runs there), but an independently-fetched
    // list on CreatorDetailsComponent/CustomerDetailsComponent (only analyticsSourceBatchState runs
    // there) - same distinction syncAnalyticsSourceFromInput's own reference check makes, so this
    // reads whichever batch's progress actually corresponds to the campaigns list just processed
    // above, instead of a single flag that either run could clobber.
    const isProcessingRelevantBatch =
      campaigns === this.store.campaigns$()
        ? this.tableBatchState.isProcessing
        : this.analyticsSourceBatchState.isProcessing;

    this.isAnalyticsCalculated.set(!isProcessingRelevantBatch && allAnalyticsDataReceived);

    this.dataSource().data = [...this.tableData];
  }

  private updateCampaignAnalytics(
    campaignId: string,
    analytics?: ICampaignAnalyticsApi | null,
    nonLiveCampaignsMap?: Map<string, TCampaignApi> | null,
  ): void {
    const campaign = this.campaigns()?.find((c) => c.id === campaignId);
    const relatedTableData = this.tableData.find((tableData) => tableData.id === campaignId);

    if (!campaign || !relatedTableData) {
      return;
    }

    let campaignAnalytics: ICampaignAnalytics;

    if (analytics) {
      [campaignAnalytics] = this.campaignAnalyticsUtilities.getCampaignAnalyticsData(campaign, analytics);
    } else {
      [campaignAnalytics] = this.campaignAnalyticsUtilities.getCampaignAnalyticsData(
        nonLiveCampaignsMap?.get(campaignId) ?? campaign,
        analytics,
      );
    }

    relatedTableData.ctaKpiStatus = {
      ...this.campaignAnalyticsUtilities.getStatusableDisplayConfig(campaignAnalytics.status),
      current:
        campaignAnalytics.ctaKpiStatus.currentOkDeliverables +
        campaignAnalytics.ctaKpiStatus.currentProblemDeliverables +
        campaignAnalytics.ctaKpiStatus.currentOkKpis +
        campaignAnalytics.ctaKpiStatus.currentProblemKpis,
      total: campaignAnalytics.ctaKpiStatus.expectedDeliverables + campaignAnalytics.ctaKpiStatus.expectedKpis,
    };
    relatedTableData.ctaStatus = {
      ...this.campaignAnalyticsUtilities.getStatusableDisplayConfig(campaignAnalytics.status),
      current:
        campaignAnalytics.ctaKpiStatus.currentOkDeliverables +
        campaignAnalytics.ctaKpiStatus.currentProblemDeliverables,
      total: campaignAnalytics.ctaKpiStatus.expectedDeliverables,
    };
    relatedTableData.kpiStatus = {
      ...this.campaignAnalyticsUtilities.getStatusableDisplayConfig(EAnalyticsStatus.ok),
      current: campaignAnalytics.ctaKpiStatus.currentOkKpis + campaignAnalytics.ctaKpiStatus.currentProblemKpis,
      total: campaignAnalytics.ctaKpiStatus.expectedKpis,
    };
  }

  private mapCampaignData(campaigns: TCampaignApi[]): TCampaignTable[] {
    this.tableData =
      campaigns?.map((campaign) => {
        return {
          ...campaign,
          ctaKpiStatus: {
            color: 'var(--shikenso-info-base)',
            current: 0,
            status: EAnalyticsStatus.processing,
            total: 0,
          },
          ctaStatus: {
            color: 'var(--shikenso-info-base)',
            current: 0,
            status: EAnalyticsStatus.processing,
            total: 0,
          },
          customerIcon: campaign.customer?.iconId,
          endFormatted: this.dateUtils.formatDate(campaign.end),
          kpiStatus: {
            color: 'var(--shikenso-success-base)',
            current: 0,
            status: EAnalyticsStatus.ok,
            total: 0,
          },
          pay:
            campaign.campaignCreators?.reduce(
              (acc, creator) =>
                acc +
                (!this.creatorId() || (this.creatorId() && creator.creatorId === this.creatorId())
                  ? (Number(creator.cost) ?? 0)
                  : 0),
              0,
            ) ?? 0,
          startFormatted: this.dateUtils.formatDate(campaign.start),
        };
      }) ?? [];

    return this.tableData;
  }

  private configureDisplayedColumns(campaigns?: TCampaignApi[] | null): void {
    if (this.columns()?.length) {
      this.datatableColumns = this.datatableColumns.filter((column) =>
        this.columns().includes(column.id as TCampaignDatatableColumn),
      );

      return;
    }

    if (
      this.creatorMode() &&
      this.router.url === '/creator' &&
      this.permissionService.hasAnyPermission([this.EPermissions.creatorReadSelf])
    ) {
      this.datatableColumns = this.datatableColumns.filter((column) =>
        ['name', 'status', 'startFormatted', 'endFormatted', 'customer', 'budget', 'ctaKpiStatus'].includes(column.id),
      );

      if (
        campaigns?.some((campaign) => (campaign.budget && campaign.budget > 0) || campaign.campaignCreators?.[0]?.cost)
      ) {
        this.datatableColumns = this.datatableColumns.filter((column) =>
          ['name', 'status', 'startFormatted', 'endFormatted', 'customer', 'pay', 'ctaKpiStatus'].includes(column.id),
        );
      }

      return;
    }

    this.datatableColumns = this.datatableColumns.filter((column) =>
      ['name', 'status', 'startFormatted', 'endFormatted', 'customer', 'budget', 'ctaKpiStatus', 'actions'].includes(
        column.id,
      ),
    );
  }

  private prepareFetchCampaignsParams(
    params: IDataRefreshRequestParams | null,
    campaignIds?: string[],
    isForAnalytics = false,
  ): IFetchCampaignsParams {
    const createdByIds = this.createdByIds();
    const createdByFilter = createdByIds.size > 0 ? { created_by__in: Array.from(createdByIds).join(',') } : {};

    const teamIds = this.teamIds();
    const teamFilter = teamIds.size > 0 ? { team_id__in: Array.from(teamIds).join(',') } : {};

    const combinedRequestFilters = {
      ...(this.requestFilters() ? { ...this.requestFilters() } : {}),
      ...createdByFilter,
      ...teamFilter,
    };

    const effectiveRequestFilters: Record<string, unknown> | null =
      Object.keys(combinedRequestFilters).length > 0 ? combinedRequestFilters : null;

    if (!params) {
      return this.campaignsDatatableService.prepareFetchCampaignsParams({
        ...(campaignIds ? { campaignIds } : {}),
        adminMode: this.adminMode(),
        creatorMode: this.creatorMode(),
        isForAnalytics,
        params,
        requestFilters: effectiveRequestFilters,
        requestJoins: this.requestJoins(),
      });
    }

    return this.campaignsDatatableService.prepareFetchCampaignsParams({
      ...(campaignIds ? { campaignIds } : {}),
      adminMode: this.adminMode(),
      creatorMode: this.creatorMode(),
      isForAnalytics,
      params,
      requestFilters: effectiveRequestFilters,
      requestJoins: this.requestJoins(),
    });
  }

  /**
   * Always processes store.campaigns$() - this datatable's own paginated/filtered/sorted fetch -
   * unconditionally, with no "skip the first tick" logic.
   *
   * An earlier version guarded this with a one-time skipInitialAnalyticsRequest flag, deferring its
   * very first call to syncAnalyticsSourceFromInput's campaigns()-based effect instead. That only
   * worked back when campaigns() and store.campaigns$() were always the same data everywhere this
   * component is used. Now that CreatorDetailsComponent/CustomerDetailsComponent feed campaigns()
   * from the independent profileCampaigns$() (see syncAnalyticsSourceFromInput below), that flag
   * kept skipping this effect's first real store.campaigns$() update for CampaignsComponent/
   * AdminCampaignsComponent while syncAnalyticsSourceFromInput (correctly) no longer picked up the
   * slack either - nothing ever called prepareTableData with real data, so the table stayed empty
   * and fetchAnalyticsForAllCampaigns never ran, which looked like the page loading indefinitely.
   * Calling prepareTableData unconditionally removes the need for any handoff between the two
   * effects: syncAnalyticsSourceFromInput's own reference check already prevents it from ever
   * double-processing the same update (see below), so there's nothing left for a skip to protect
   * against.
   */
  private syncTableDataFromStore(): void {
    this.prepareTableData(this.store.campaigns$());
  }

  /**
   * campaigns() and store.campaigns$() are both read unconditionally, before the guard below, so
   * this effect keeps tracking both regardless of which branch runs this time - effect() only
   * tracks signals actually read on the MOST RECENT run (dependencies aren't accumulated across
   * runs), so reading store.campaigns$() only on one branch would silently drop it as a dependency
   * the moment the other branch started running instead.
   *
   * Deliberately calls prepareAnalyticsSourceData rather than prepareTableData - for
   * CreatorDetailsComponent/CustomerDetailsComponent, campaigns() is their own independently-
   * fetched full CTA-joined list, not this component's own paginated/filtered/sorted fetch
   * (store.campaigns$(), always handled by syncTableDataFromStore above), so letting it also drive
   * dataSource() meant whichever of the two fetches resolved last silently replaced the other's
   * rows.
   *
   * The guard below compares campaigns() and store.campaigns$() by reference instead of relying on
   * a mutable flag: CampaignsComponent and AdminCampaignsComponent bind campaigns() directly to
   * store.campaigns$() (same CampaignsStore instance, same field), so any update is already fully
   * handled by syncTableDataFromStore's own prepareTableData call above, and this effect only needs
   * to skip its own, otherwise-redundant, second fetchAnalyticsForAllCampaigns kickoff for that same
   * update. CreatorDetailsComponent/CustomerDetailsComponent instead bind campaigns() to the
   * independently-fetched profileCampaigns$(), which is never the same array as store.campaigns$(),
   * so this effect is the only place that ever processes it and must never be skipped.
   */
  private syncAnalyticsSourceFromInput(): void {
    const campaigns = this.campaigns();
    const storeCampaigns = this.store.campaigns$();

    if (campaigns === storeCampaigns) {
      return;
    }

    this.prepareAnalyticsSourceData(campaigns);
  }

  private prepareTableData(campaigns: TCampaignApi[] | null): void {
    this.configureDisplayedColumns(campaigns);

    if (campaigns !== null && campaigns !== undefined) {
      this.dataSource().data = this.mapCampaignData(campaigns);

      if (campaigns.length > 0) {
        this.fetchAnalyticsForAllCampaigns(campaigns, this.tableBatchState);
      }
    }
  }

  /**
   * Mirrors prepareTableData's column-config + analytics-batch-kickoff side effects, but
   * deliberately does NOT touch dataSource().data - see syncAnalyticsSourceFromInput above for
   * why.
   */
  private prepareAnalyticsSourceData(campaigns: TCampaignApi[] | null): void {
    this.configureDisplayedColumns(campaigns);

    if (campaigns?.length) {
      this.fetchAnalyticsForAllCampaigns(campaigns, this.analyticsSourceBatchState);
    }
  }

  private setDatatableConfig(): void {
    this.datatableConfig = {
      filters: [
        {
          key: 'status__in',
          label: `${this.translate.instant('field.status')}`,
          multiple: true,
          options: [
            {
              label: this.translate.instant(`shared.statuses.${camelCase(ECampaignStatus.draft)}`),
              value: ECampaignStatus.draft,
            },
            {
              label: this.translate.instant(`shared.statuses.${camelCase(ECampaignStatus.planned)}`),
              value: ECampaignStatus.planned,
            },
            {
              label: this.translate.instant(`shared.statuses.${camelCase(ECampaignStatus.live)}`),
              value: ECampaignStatus.live,
            },
            {
              label: this.translate.instant(`shared.statuses.${camelCase(ECampaignStatus.ended)}`),
              value: ECampaignStatus.ended,
            },
            {
              label: this.translate.instant(`shared.statuses.${camelCase(ECampaignStatus.archived)}`),
              value: ECampaignStatus.archived,
            },
          ],
          placeholder: `${this.translate.instant('shared.all')}`,
        },
      ],
      isClientSide: false,
      skipInitialRequest: false,
      storageKey: this.buildStorageKey(),
    } satisfies IDatatableConfiguration<TCampaignTable>;
  }

  private buildStorageKey(): string {
    const modeKey: TCampaignStorageMode = this.adminMode() ? 'admin' : this.creatorMode() ? 'creator' : 'default';
    const creatorKey: string = this.creatorMode() && this.creatorId() ? String(this.creatorId()) : 'all';
    const tableKey: TCampaignStorageKey = `${modeKey}:${creatorKey}`;

    return this.tableStateService.buildKey('campaigns', tableKey);
  }
}
